import { Injectable } from '@angular/core';
import { CuadrajeQuantity, CuadrajeSummary } from '@app/models/production/cuadraje';
import { BalanceoAvailableRoll, BalanceoRollLocation, BalanceoSummary } from '@app/models/production/balanceo';
import { CustomerPurchaseOrderCuadrajeDto } from '@app/models/production/cuadraje-dto';
import { ProductionCardBalanceDto } from '@app/models/production/balance-dto';
import { unitOfMeasureToDisplayUnit } from '@app/utilities/unit-of-measure.util';

/**
 * Every Work Order block of a row is the same Item + Color, so they all carry Sierra's primary
 * color and read as one quantity split into blocks — the meter's track-colored gap is what
 * separates them. `--primary-color` is the library's `$primary-color`, not PrimeNG's `primary.500`.
 */
const CONTRIBUTION_COLOR = 'var(--primary-color)';

/**
 * Maps the real Cuadraje/Balanceo DTOs (Production.API) to the view models the mock-driven modals
 * (`CuadrajeSummary` / `BalanceoSummary`) already render. No system field carries an actual color
 * swatch, so `colorHex` is a deterministic hash of the color reference/name — stable, not the real
 * Pantone value.
 */
@Injectable({ providedIn: 'root' })
export class CuadrajeBalanceoAdapterService {
  toCuadrajeSummary(dto: CustomerPurchaseOrderCuadrajeDto): CuadrajeSummary {
    return {
      // Shown as the panel header — the PO can span several Work Orders, so both are named.
      workOrderNumber: `PO ${dto.productOrderNumber} (${dto.workOrderNumbers.join(', ')})`,
      customerPoNumber: dto.productOrderNumber,
      unit: 'lbs',
      overage: dto.clientOveragePercentage,
      shortage: dto.clientShortagePercentage,
      colors: dto.colors.map((colorGroup) => ({
        colorReference: colorGroup.colorReference,
        colorName: this.withLabDip(colorGroup.colorName || colorGroup.colorReference, colorGroup.colorLabDipNumber),
        colorHex: '#828282',
        fabrics: colorGroup.fabrics.map((fabric) => {
          const target = fabric.requiredQuantityInPounds;
          const toleranceMax =
            dto.clientOveragePercentage != null ? target * (1 + dto.clientOveragePercentage / 100) : Number.MAX_SAFE_INTEGER;
          const toleranceMin = dto.clientShortagePercentage != null ? target * (1 + dto.clientShortagePercentage / 100) : 0;
          // The target/tolerance have no rolls yet to sum yards from — only these three still go
          // through the fabric's conversion factor. 0 means the Fabric has no PoundToYard set in
          // Item.API — yards stays 0 for this row rather than throwing, lbs is still fully usable.
          const targetQuantity = (lbs: number): CuadrajeQuantity => ({ lbs, yds: lbs * fabric.poundsToYardsFactor });

          return {
            fabricType: fabric.fabricType || fabric.fabricCode,
            fabricCode: fabric.fabricCode,
            // No composition data in this DTO — the type label is the best available fallback.
            fabricDescription: fabric.fabricType || fabric.fabricCode,
            target: targetQuantity(target),
            toleranceMin: targetQuantity(toleranceMin),
            toleranceMax: targetQuantity(toleranceMax),
            contributions: fabric.workOrderContributions.map((contribution) => ({
              workOrderNumber: contribution.workOrderNumber,
              quantity: { lbs: contribution.approvedQuantityInPounds, yds: contribution.approvedQuantityInYards },
              color: CONTRIBUTION_COLOR,
            })),
            leftOver: { lbs: fabric.leftOverQuantityInPounds, yds: fabric.leftOverQuantityInYards },
            onHold: { lbs: fabric.onHoldQuantityInPounds, yds: fabric.onHoldQuantityInYards },
          };
        }),
      })),
    };
  }

  /** Appends " - {LabDip}" to a color's display name, when the color has one. */
  private withLabDip(colorName: string, labDipNumber: string | undefined): string {
    return labDipNumber ? `${colorName} - ${labDipNumber}` : colorName;
  }

  toBalanceoSummary(dto: ProductionCardBalanceDto): BalanceoSummary {
    // Any non-anchor fabric represents "what Option B is looking for" well enough for the label —
    // the table itself lists leftovers from every fabric on the card, anchor included.
    const representativeFabric = dto.fabrics.find((f) => !f.isAnchor) ?? dto.fabrics[0];
    const unit = unitOfMeasureToDisplayUnit(dto.workOrderUnitOfMeasure);

    const seenRollNumbers = new Set<string>();
    const availableRolls: BalanceoAvailableRoll[] = dto.fabrics
      .flatMap((f) => f.reassignableLeftovers)
      .filter((roll) => {
        if (seenRollNumbers.has(roll.rollNumber)) return false;
        seenRollNumbers.add(roll.rollNumber);
        return true;
      })
      .map((roll) => ({
        rollNumber: roll.rollNumber,
        weightLbs: roll.weightInLbs,
        location: (roll.currentLocationName as BalanceoRollLocation) ?? 'Rolling Warehouse',
        originProductionCardNumber: roll.originProductionCardNumber ?? '',
        originProductionCardId: roll.originProductionCardId,
      }));

    const colorLabel = this.withLabDip(dto.colorName || dto.colorReference || dto.productionCardNumber, dto.colorLabDipNumber);

    return {
      productionCardNumber: dto.productionCardNumber,
      productionCardId: dto.productionCardId,
      unit,
      leftOverFabricCode: representativeFabric?.fabricCode ?? '',
      leftOverColorName: colorLabel,
      colors: [
        {
          colorName: colorLabel,
          colorHex: '#828282',
          fabrics: dto.fabrics.map((fabric) => {
            // `plan` has no rolls to sum yards from yet, so it still goes through the fabric's
            // conversion factor. `real` is a genuine sum of each approved roll's own
            // FinalLengthInYards (same principle as Cuadraje) — never derived from the factor.
            const plan = unit === 'yds' ? fabric.plannedQuantityInPounds * fabric.poundsToYardsFactor : fabric.plannedQuantityInPounds;
            const real = unit === 'yds' ? fabric.approvedQuantityInYards : fabric.approvedQuantityInPounds;

            return {
              fabricType: fabric.fabricType || fabric.fabricCode,
              fabricCode: fabric.fabricCode,
              isAnchor: fabric.isAnchor,
              plan,
              real,
              targetRatio: fabric.targetRatioPercentage,
            };
          }),
        },
      ],
      availableRolls,
    };
  }
}
